import { useMemo, useState } from "react";
import { useAppSelector } from "../../redux/store/hooks";
import {
  useGetBrokersQuery,
  useGetOrganizationsQuery,
  useGetPlansQuery,
} from "../../redux/api";
import type { Organization } from "../../redux/api";
import { IconSearch } from "../../components/icons";

const COLUMN_COUNT = 5;

const STATUS_TONE: Record<string, string> = {
  active: "badge--success",
  trial: "badge--info",
  suspended: "badge--warning",
  expired: "badge--danger",
  deleted: "badge--danger",
};

const statusTone = (status?: string | null) =>
  STATUS_TONE[(status ?? "").toLowerCase()] ?? "badge--info";

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "—";

// Broker → Referrals. Organizations the signed-in broker referred.
const BrokerReferrals = () => {
  const email = useAppSelector((s) => s.layout.user.email);

  const { data: brokers = [] } = useGetBrokersQuery();
  const { data: organizations = [] } = useGetOrganizationsQuery();
  const { data: plans = [] } = useGetPlansQuery();

  const [search, setSearch] = useState("");

  const broker = brokers.find(
    (b) => (b.email ?? "").toLowerCase() === email.toLowerCase(),
  );

  const planName = (id?: string | null) =>
    id ? plans.find((p) => p.id === id)?.name ?? id : "—";

  const referred = useMemo(
    () => organizations.filter((o) => broker && o.brokerId === broker.id),
    [organizations, broker],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return referred;
    return referred.filter((o: Organization) =>
      [o.name, o.ownerEmail, o.status].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [referred, search]);

  return (
    <div className="dashboard">
      {!broker ? (
        <section className="panel">
          <div className="panel__head">
            <div>
              <h2>My Referrals</h2>
            </div>
          </div>
          <div className="profile-empty">
            No broker account is linked to {email}.
          </div>
        </section>
      ) : (
        <section className="panel">
          <div className="panel__head">
            <div>
              <h2>My Referrals</h2>
              <p className="panel__sub">
                Showing {visible.length} of {referred.length}
              </p>
            </div>
            <div className="toolbar">
              <div className="search">
                <span className="search__icon">
                  <IconSearch />
                </span>
                <input
                  type="search"
                  placeholder="Search organizations…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Owner</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                      No referred organizations yet.
                    </td>
                  </tr>
                ) : (
                  visible.map((org: Organization) => (
                    <tr key={org.id}>
                      <td className="cell-title">{org.name}</td>
                      <td className="cell-date">{org.ownerEmail}</td>
                      <td>
                        <span className="pill pill--neutral">
                          {planName(org.planId)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${statusTone(org.status)}`}>
                          <span className="badge__dot" />
                          {org.status ?? "—"}
                        </span>
                      </td>
                      <td className="cell-date">{formatDate(org.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default BrokerReferrals;
